import { Component, Injector, OnInit, ViewChild, forwardRef, input, model, inject } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { OtpVerificationService } from '@finxone-platform/shared/services';
import { FieldVerificationService } from '../services/field-verification.service';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  InputFormatType,
  NavigationOptionsType,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Subject, takeUntil } from 'rxjs';
import { FinxoneInputComponent } from '../finxone-input/finxone-input.component';
import { ValidationConfig } from '../input-field-validation/validation-config.type';

@Component({
  standalone: false,
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'finxone-email',
  templateUrl: './finxone-email.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FinxoneEmailComponent),
      multi: true,
    },
  ],
})
export class FinxoneEmailComponent implements ControlValueAccessor, OnInit {
  private injector = inject(Injector);
  private otpVerificationService = inject(OtpVerificationService);
  private fieldVerification = inject(FieldVerificationService);

  @ViewChild(FinxoneInputComponent) finxoneInput!: FinxoneInputComponent;
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  // General Input Configuration
  public type = input<string>('email');
  public placeholder = input<string>('');
  public label = input<string>('');
  public inputMode = input<string>('email');
  public id = input<string>('');
  public name = input<string>('');
  public infoText = input<string>('');
  public inputLabelTooltipText = input<string>('');

  // Button Inputs
  public isVerifyButtonShown = model<boolean>(false);
  public isVerifyButtonGlobalStylingAllowed = input<boolean>(false);
  public verifyButtonGlobalStylingClass = input<string>('');

  public verifyButtonStyle = input<baseCssConfigStyle>({});
  public verifyButtonTextContent = input<string>('');
  public disableAutocomplete = input<boolean>(false);

  // Validation Configuration
  public validationConfig = input<ValidationConfig[]>([]);

  public inputFormat = input<InputFormatType>();
  // Parent form control
  public parentControl = input<FormControl>();

  public spaceBetweenElementUnit = input('');
  public spaceBetweenElementValue = input(0);

  public parentFormId = input<string>(''); // onboardingFlow
  public nestedFormId = input<string>(''); // onboardingFlow.customForm-sign-up
  public formGroup = input.required<FormGroup>();
  public formControlName = input.required<string>();

  public navigationOptions = input<NavigationOptionsType>({} as NavigationOptionsType);

  ngOnInit(): void {
    this.fieldVerification.handleEmailVerification(
      this.injector,
      this.destroy$,
      this.isVerifyButtonShown,
      this.parentFormId(),
      this.formGroup(),
      this.formControlName(),
    );

    this.otpVerificationService.otpVerified$.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data.field !== 'email') return;

      this.formGroup().get('isEmailVerified')?.setValue(true);
      this.fieldVerification.handleEmailVerification(
        this.injector,
        this.destroy$,
        this.isVerifyButtonShown,
        this.parentFormId(),
        this.formGroup(),
        this.formControlName(),
      );
    });
  }

  // Propagate to inner input
  public setDisabledState(isDisabled: boolean): void {
    if (this.finxoneInput) {
      this.finxoneInput.setDisabledState(isDisabled);
    }
  }

  writeValue(value: string): void {
    if (this.finxoneInput) this.finxoneInput.writeValue(value);
  }

  registerOnChange(fn: any): void {
    if (this.finxoneInput) this.finxoneInput.registerOnChange(fn);
  }

  registerOnTouched(fn: any): void {
    if (this.finxoneInput) this.finxoneInput.registerOnTouched(fn);
  }

  public verifyEmail() {
    this.fieldVerification.verifyEmailPhoneButton(
      this.injector,
      this.inputFormat(),
      this.navigationOptions(),
      this.formControlName(),
      this.parentFormId(),
      this.nestedFormId(),
      false,
      undefined,
      this.formGroup()?.get(this.formControlName()),
      undefined,
    );
  }
}
